import styled from '@emotion/styled'
import { MappedName, SiteMetricsByTagId } from '@pubstack/common/src/analytics/query'
import { CurrencySymbol } from '@pubstack/common/src/currency'
import { DeviceType } from '@pubstack/common/src/device-type'
import { FunctionComponent } from 'react'
import { useForm, useWatch } from 'react-hook-form'
import { Sizes } from '~/assets/style/tokens'
import { MultipleDetailedDataTable } from '~/components/DataTable/DataTable'
import { Input } from '~/components/Input'
import { RadioGroup } from '~/components/RadioGroup'
import { Tooltip } from '~/components/Tooltip'
import { Widget, WidgetProps } from '~/components/Widget'
import { useAnalyticsDataTable } from '~/modules/analytics/analyticsDataTable.hooks'
import { cwvCls, cwvFcp, cwvInp, cwvLcp, cwvTtfb } from '~/modules/analytics/common/formulas'
import { WithClassName } from '~/types/utils'
import { ANALYTICS_TOOLTIPS } from '~/utils/constants'

const Group = styled.div`
  display: flex;
  flex-direction: row;
  gap:  ${Sizes[8]};
  align-items: baseline;
  margin: ${Sizes[16]} 0;
  ${Tooltip} {
    align-self: center;
  }
`

type PureSitesOverviewTableProps = WithClassName &
  Omit<WidgetProps, 'title' | 'info' | 'icon' | 'refreshTrackingLabel'> & {
    rawData?: SiteMetricsByTagId
    onRowClick?: (name: MappedName) => void
    currencySymbol: CurrencySymbol
    onSortChange: (metric: string) => void
    onDeviceSelected: (device: DeviceType) => void
  }

export const PureCoreWebVitalsOverviewTable: FunctionComponent<PureSitesOverviewTableProps> = ({ rawData, onRowClick, currencySymbol, onSortChange, onDeviceSelected, ...props }) => {
  const { control } = useForm<{ device: DeviceType; search: string }>({ defaultValues: { device: 'desktop', search: '' } })

  const search = useWatch({ control, name: 'search' })

  const { data, columns, sorting } = useAnalyticsDataTable({
    rawData,
    currencySymbol,
    filterData: ([data]) => {
      return data.name.label.toString().toLowerCase().includes(search.toLowerCase())
    },
    dataConfigs: [
      [
        {
          displayedAs: 'text',
          sortLabel: 'Name',
        },
        {
          formula: cwvLcp,
          propertyName: 'cwvLcp',
          status: {
            warning: 2500,
            alert: 4000,
          },
          tooltip: ANALYTICS_TOOLTIPS.CWV_LCP,
        },
        {
          formula: cwvInp,
          propertyName: 'cwvInp',
          status: {
            warning: 200,
            alert: 500,
          },
          tooltip: ANALYTICS_TOOLTIPS.CWV_INP,
        },
        {
          formula: cwvCls,
          propertyName: 'cwvCls',
          status: {
            warning: 0.1,
            alert: 0.25,
          },
          tooltip: ANALYTICS_TOOLTIPS.CWV_CLS,
        },
        {
          formula: cwvFcp,
          propertyName: 'cwvFcp',
          status: {
            warning: 1800,
            alert: 3000,
          },
          tooltip: ANALYTICS_TOOLTIPS.CWV_FCP,
        },
        {
          formula: cwvTtfb,
          propertyName: 'cwvTtfb',
          status: {
            warning: 800,
            alert: 1800,
          },
          tooltip: ANALYTICS_TOOLTIPS.CWV_TTFB,
        },
      ],
    ],
    columnsConfigs: ['name', 'cwvLcp', 'cwvInp', 'cwvCls', 'cwvFcp', 'cwvTtfb'],
    defaultSort: 'name',
  })
  return (
    <>
      <Group>
        <Input name={'search'} type={'text'} iconLeft={'search'} labelIsPlaceholder label={'Search'} control={control} />
        <RadioGroup
          control={control}
          onChange={(value) => onDeviceSelected(value as DeviceType)}
          name={'device'}
          options={[
            {
              label: 'Desktop',
              value: 'desktop',
              iconName: 'desktop',
              trackingLabel: 'ana-cwv-overview-set',
            },
            {
              label: 'Mobile',
              value: 'mobile',
              iconName: 'mobile',
              trackingLabel: 'ana--cwv-overview--set',
            },
          ]}
        />
      </Group>
      <Widget {...props} icon={'fire'} title={'Breakdown'} refreshTrackingLabel={'ana-cwv-overview-refresh'}>
        <MultipleDetailedDataTable rows={data} sorting={sorting} columns={columns} onSortChange={(option) => onSortChange(option.value as string)} />
      </Widget>
    </>
  )
}
